import React, { useState } from "react";
import { Calendar, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, isValid } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";

interface PackageFiltersProps {
  statusFilter: string;
  setStatusFilter: (s: string) => void;
  startDate: string | null;
  setStartDate: (d: string | null) => void;
  endDate: string | null;
  setEndDate: (d: string | null) => void;
}

const statuses = ["All", "Pending", "Delivered", "Deleted", "Discarded"];

const PackageFilters: React.FC<PackageFiltersProps> = ({
  statusFilter,
  setStatusFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate
}) => {
  // Date range picker state (keep controlled locally to avoid flickering)
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [internalRange, setInternalRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: startDate ? new Date(startDate) : null,
    to: endDate ? new Date(endDate) : null,
  });

  const hasActiveFilters =
    statusFilter !== "All" ||
    startDate !== null ||
    endDate !== null;

  const handleRangeChange = (range: { from?: Date; to?: Date }) => {
    const fromValid = range.from && isValid(range.from) ? range.from : null;
    const toValid = range.to && isValid(range.to) ? range.to : null;
    setInternalRange({ from: fromValid, to: toValid });
    setStartDate(fromValid ? fromValid.toISOString() : null);
    setEndDate(toValid ? toValid.toISOString() : null);
  };

  return (
    <div className="flex flex-wrap gap-2 sm:items-center min-w-0">
      {/* Date Range Picker */}
      <Popover open={dateRangeOpen} onOpenChange={setDateRangeOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "flex items-center w-[160px] justify-start",
              (internalRange.from || internalRange.to) && "font-semibold"
            )}
          >
            <Calendar className="mr-2 h-4 w-4" />
            {internalRange.from && internalRange.to
              ? `${format(internalRange.from, "PP")} - ${format(
                  internalRange.to,
                  "PP"
                )}`
              : "Date range"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={{
              from: internalRange.from ? internalRange.from : undefined,
              to: internalRange.to ? internalRange.to : undefined,
            }}
            onSelect={(range) => handleRangeChange(range)}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
            numberOfMonths={1}
          />
          <div className="px-3 pb-3 pt-1 flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setInternalRange({ from: null, to: null });
                setStartDate(null);
                setEndDate(null);
              }}
            >
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {/* Status Filter */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="flex items-center">
            <Filter className="mr-2 h-4 w-4" />
            {statusFilter === "All" ? "All packages" : statusFilter}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="start">
          <div className="space-y-2">
            {statuses.map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? "default" : "outline"}
                className="w-full justify-between"
                onClick={() => setStatusFilter(status)}
              >
                <span>{status === "All" ? "All packages" : status}</span>
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setInternalRange({ from: null, to: null });
            setStartDate(null);
            setEndDate(null);
            setStatusFilter("All");
          }}
          className="flex items-center px-2 py-1 h-8 text-xs"
        >
          <X className="mr-1 h-3 w-3" />
          Clear
        </Button>
      )}
    </div>
  );
};

export default PackageFilters;
